import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  useGetFieldAdminHSVehicleQuery,
  useSaveFieldAdminHSVehicleMutation,
  useUploadFieldAdminHSVehiclePhotoMutation,
  useGetFieldAdminVehiclesQuery,
} from "../fieldAdminApi";
import { CARD_BG, BORDER, AMBER } from "../../../Global/colors";
import { parseLocalDate } from "../designTokens";
import { useDispatch } from "react-redux";
import { showSuccess, showError } from "../../../Global/notifications/notificationsSlice";
import { HSPageShell, HSSectionLabel, HSNotesBlock, HSFooterActions } from "./HSStepShell";

import { FAErrorCard, FASkeletonRows } from "../FAStates";
import { FACheckbox, useConfirm } from "../FAControls";
import { resolveMediaUrl } from "../mediaUrl";
import { OdometerDialog } from "./VehicleOdometerDialog";

const CHECKLIST_LABELS = [
  "Tyres condition",
  "Tyre pressure",
  "Headlights & indicators",
  "Windshield & wipers",
  "Fire extinguisher (in date)",
  "First aid kit",
  "Safety triangle / cones",
  "Engine oil",
  "Coolant",
  "Brake fluid",
  "Diesel level",
  "Spare wheel & jack",
];

const PHOTO_SLOTS = [
  { slot: "FRONT", label: "Front" },
  { slot: "SIDE", label: "Side" },
  { slot: "ODOMETER", label: "Odometer reading" },
];

// last_serviced is a plain "YYYY-MM-DD" DateField — parsed bare, "2026-04-11" is UTC
// midnight, which renders as 10 April in any negative-offset timezone.
function formatServiceDate(dateStr) {
  if (!dateStr) return null;
  return parseLocalDate(dateStr).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export default function Vehicle() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data, isSuccess, isError, refetch } = useGetFieldAdminHSVehicleQuery();
  const { data: fleetData, isSuccess: isFleetSuccess } = useGetFieldAdminVehiclesQuery();
  const [saveVehicle, { isLoading: isSaving }] = useSaveFieldAdminHSVehicleMutation();
  const [uploadPhoto] = useUploadFieldAdminHSVehiclePhotoMutation();

  const [vehicleId, setVehicleId] = useState("");
  const [odometerKm, setOdometerKm] = useState("");
  const [items, setItems] = useState([]);
  const [sectionNotes, setSectionNotes] = useState("");
  const [openNoteFor, setOpenNoteFor] = useState(null);
  const [odometerModalOpen, setOdometerModalOpen] = useState(false);
  const { ask, confirmDialog } = useConfirm();
  const fileInputRef = useRef(null);
  const pendingSlot = useRef(null);

  // The vehicle currently assigned to this FA's team (Fleet enforces one at a time) is
  // unambiguously "the vehicle being inspected today" — once one exists, the card reads
  // real make/model/year/fuel/service data instead of the free-text fallback below.
  const assignedVehicle = isFleetSuccess
    ? fleetData.vehicles.find((v) => v.assigned_team_id === fleetData.team_id)
    : null;

  // Hydrate exactly once. This used to depend on assignedVehicle, which resolves from a
  // separate query — so when the fleet call landed after the FA had started ticking boxes
  // (or on any RTK refetch), the effect re-ran and reset items, silently discarding them.
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (!isSuccess || hydratedRef.current) return;
    hydratedRef.current = true;
    setVehicleId(data.vehicle_id || "");
    setOdometerKm(data.odometer_km ?? "");
    setSectionNotes(data.notes || "");
    setItems(
      data.items && data.items.length
        ? data.items.map((item) => ({ label: item.label, checked: item.checked, note: item.note || "" }))
        : CHECKLIST_LABELS.map((label) => ({ label, checked: false, note: "" }))
    );
  }, [isSuccess, data]);

  // The register's reading is only a starting point for a day with no odometer captured
  // yet. Backfill it whenever the fleet query resolves, but never over a value the FA has
  // already typed — which is what makes this safe to re-run.
  useEffect(() => {
    if (!hydratedRef.current) return;
    if (data?.odometer_km != null || assignedVehicle?.odometer_km == null) return;
    setOdometerKm((prev) => (prev === "" ? String(assignedVehicle.odometer_km) : prev));
  }, [data, assignedVehicle]);

  if (!isSuccess || !items.length) {
    return (
      <HSPageShell step={{ stepNumber: 1, title: "Vehicle", countLabel: "" }}>
        {isError ? <FAErrorCard onRetry={refetch} /> : <FASkeletonRows count={6} />}
      </HSPageShell>
    );
  }

  const checkedCount = items.filter((item) => item.checked).length;
  const rawOdometer = odometerKm !== "" ? odometerKm : assignedVehicle?.odometer_km;
  const odometerReading = rawOdometer == null || rawOdometer === "" ? null : Number(rawOdometer);
  const photosBySlot = {};
  (data.photos || []).forEach((photo) => { photosBySlot[photo.slot] = photo; });

  const toggleItem = (index) => {
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, checked: !item.checked } : item)));
  };

  const updateNote = (index, note) => {
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, note } : item)));
  };

  const buildPayload = (complete) => ({
    // Always the register's own value when a vehicle is assigned — never the locally
    // typed one, so this can't drift from Fleet's own spelling of the same plate.
    vehicle_id: assignedVehicle ? assignedVehicle.registration : (vehicleId || null),
    odometer_km: odometerKm === "" ? null : Number(odometerKm),
    notes: sectionNotes,
    items,
    complete,
  });

  const handleSaveExit = async () => {
    const result = await saveVehicle(buildPayload(false));
    return !result.error;
  };

  const handleOdometerSave = async (draftValue) => {
    const result = await saveVehicle({ ...buildPayload(false), odometer_km: draftValue === "" ? null : Number(draftValue) });
    if (!result.error) {
      setOdometerKm(draftValue);
      setOdometerModalOpen(false);
    }
  };

  const handleMarkComplete = async () => {
    if (checkedCount < items.length) {
      const proceed = await ask({
        title: "Complete with unchecked items?",
        body: `${items.length - checkedCount} of ${items.length} checklist items are not yet ticked. Mark the vehicle inspection complete anyway?`,
        confirmLabel: "Mark complete",
        cancelLabel: "Keep checking",
        danger: true,
      });
      if (!proceed) return;
    }
    const result = await saveVehicle(buildPayload(true));
    if (result.error) return;
    dispatch(showSuccess("Vehicle inspection saved to fleet register"));
    navigate("/field-admin/hs/tools/");
  };

  const handlePhotoTileClick = (slot) => {
    pendingSlot.current = slot;
    fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file || !pendingSlot.current) return;
    // A failed upload was entirely silent — the tile just stayed empty, which is
    // indistinguishable from not having tapped it.
    const result = await uploadPhoto({ slot: pendingSlot.current, file });
    if (result.error) dispatch(showError("Photo upload failed — try again."));
  };

  return (
    <HSPageShell step={{ stepNumber: 1, title: "Vehicle", countLabel: `${checkedCount} / ${items.length} checked` }}>
      <input ref={fileInputRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={handleFileChange} />

      {assignedVehicle ? (
        // Real, sourced data now that a vehicle is assigned via Fleet — matches the
        // design's read-only headline + meta-line + plate-badge card exactly. Odometer
        // is captured via the pencil icon's modal rather than inline, so nothing on the
        // card itself reads as editable.
        <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", p: "16px 18px", display: "flex", alignItems: "center", gap: "14px", mb: "22px", flexWrap: "wrap" }}>
          {/* design .vehicle-icon is blue (rgba(59,130,246,0.12) / #93C5FD), not amber */}
          <Box sx={{ width: "46px", height: "46px", borderRadius: "10px", backgroundColor: "rgba(59,130,246,0.12)", color: "#93C5FD", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <DirectionsCarFilledOutlinedIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: "160px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Typography sx={{ fontSize: "0.98rem", fontWeight: 700, color: "#fff" }}>{assignedVehicle.make_model}</Typography>
              <IconButton
                onClick={() => setOdometerModalOpen(true)}
                size="small"
                aria-label="Update odometer"
                sx={{ p: "3px", color: "rgba(255,255,255,0.4)", "&:hover": { color: AMBER, backgroundColor: "rgba(255,255,255,0.05)" } }}
              >
                <EditOutlinedIcon sx={{ fontSize: "0.9rem" }} />
              </IconButton>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", mt: "4px", fontSize: "0.76rem", color: "rgba(255,255,255,0.5)" }}>
              {assignedVehicle.year ? (
                <>
                  <span>{assignedVehicle.year}</span>
                  <MetaDot />
                </>
              ) : null}
              {/* Omitted entirely when unknown — the old fallback rendered a fabricated "0 km". */}
              {odometerReading != null ? (
                <>
                  <span>{odometerReading.toLocaleString()} km</span>
                  <MetaDot />
                </>
              ) : null}
              <span>{assignedVehicle.fuel}</span>
              {assignedVehicle.last_serviced ? (
                <>
                  <MetaDot />
                  <span>Last serviced · {formatServiceDate(assignedVehicle.last_serviced)}</span>
                </>
              ) : null}
            </Box>
          </Box>
          <Typography
            sx={{
              fontFamily: "inherit",
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: "6px",
              padding: "6px 10px",
              fontSize: "0.78rem",
              fontWeight: 700,
              letterSpacing: "0.04em",
              color: "rgba(255,255,255,0.85)",
              textAlign: "center",
              flexShrink: 0,
            }}
          >
            {assignedVehicle.registration}
          </Typography>
        </Box>
      ) : (
        // No vehicle assigned to this team via Fleet — nothing to source display data
        // from, so this stays the fully editable fallback (same reasoning as when this
        // card was first built: an input is the only way this data can be captured at
        // all until a vehicle is assigned).
        <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", p: "16px 18px", display: "flex", alignItems: "center", gap: "14px", mb: "22px", flexWrap: "wrap" }}>
          {/* design .vehicle-icon is blue (rgba(59,130,246,0.12) / #93C5FD), not amber */}
          <Box sx={{ width: "46px", height: "46px", borderRadius: "10px", backgroundColor: "rgba(59,130,246,0.12)", color: "#93C5FD", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <DirectionsCarFilledOutlinedIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: "160px" }}>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: "5px" }}>
              <Box
                component="input"
                type="number"
                value={odometerKm}
                onChange={(e) => setOdometerKm(e.target.value)}
                placeholder="Odometer"
                sx={{
                  background: "transparent",
                  border: "none",
                  borderBottom: "1px dashed rgba(255,255,255,0.15)",
                  outline: "none",
                  color: "#fff",
                  fontFamily: "inherit",
                  fontSize: "0.98rem",
                  fontWeight: 700,
                  width: "100px",
                  p: 0,
                  pb: "1px",
                  "&::placeholder": { color: "rgba(255,255,255,0.35)", fontWeight: 500 },
                  "&:focus": { borderBottomColor: AMBER, borderBottomStyle: "solid" },
                  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 },
                  "&[type=number]": { MozAppearance: "textfield" },
                }}
              />
              <Typography component="span" sx={{ fontSize: "0.98rem", fontWeight: 700, color: "#fff", flexShrink: 0 }}>km</Typography>
            </Box>
            <Typography sx={{ fontSize: "0.76rem", color: "rgba(255,255,255,0.5)", mt: "3px" }}>Odometer reading</Typography>
          </Box>
          <Box
            component="input"
            value={vehicleId}
            onChange={(e) => setVehicleId(e.target.value)}
            placeholder="Reg / ID"
            sx={{
              fontFamily: "inherit",
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: "6px",
              padding: "6px 10px",
              fontSize: "0.78rem",
              fontWeight: 700,
              letterSpacing: "0.04em",
              color: "rgba(255,255,255,0.85)",
              textAlign: "center",
              outline: "none",
              flexShrink: 0,
              width: "128px",
              "&::placeholder": { color: "rgba(255,255,255,0.4)", fontWeight: 500, letterSpacing: "normal" },
              "&:focus": { borderColor: "rgba(253,185,64,0.5)" },
            }}
          />
        </Box>
      )}

      <HSSectionLabel right={`${items.length} items`}>Inspection checklist</HSSectionLabel>
      <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", overflow: "hidden" }}>
        {items.map((item, index) => (
          <Box key={item.label} sx={{ borderBottom: index === items.length - 1 ? "none" : "1px solid rgba(255,255,255,0.04)" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", px: "10px", py: "6px" }}>
              <FACheckbox
                checked={item.checked}
                onChange={() => toggleItem(index)}
                label={item.label}
                // .check-label is flex: 1 in the design, which is what pins + Add note to the
                // right edge. flexGrow on the label alone did nothing: the checkbox wrapper
                // around it is inline-flex, so the text was already filling a box sized to the
                // text. The row-level grow has to be on the wrapper.
                sx={{ flexGrow: 1, minWidth: 0 }}
                labelSx={{ flexGrow: 1, color: "rgba(255,255,255,0.88)", fontSize: "0.9rem" }}
              />
              <Box
                component="button"
                type="button"
                onClick={() => setOpenNoteFor(openNoteFor === index ? null : index)}
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 500,
                  fontFamily: "inherit",
                  color: item.note ? AMBER : "rgba(255,255,255,0.5)",
                  backgroundColor: item.note ? "rgba(253,185,64,0.05)" : "transparent",
                  border: `1px solid ${item.note ? "rgba(253,185,64,0.3)" : "rgba(255,255,255,0.1)"}`,
                  borderRadius: "6px",
                  px: "10px",
                  py: "5px",
                  cursor: "pointer",
                  flexShrink: 0,
                  transition: "color 0.15s, border-color 0.15s",
                  "&:hover": { color: AMBER, borderColor: "rgba(253,185,64,0.3)" },
                }}
              >
                {openNoteFor === index ? "− Hide note" : item.note ? "Note ✓" : "+ Add note"}
              </Box>
            </Box>
            {openNoteFor === index ? (
              <Box sx={{ px: "16px", pb: "12px" }}>
                <textarea
                  value={item.note}
                  onChange={(e) => updateNote(index, e.target.value)}
                  placeholder="e.g. Front-left at minimum tread — schedule replacement next week."
                  rows={2}
                  style={{ width: "100%", boxSizing: "border-box", resize: "vertical", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.1)", backgroundColor: "#0d1117", color: "#fff", padding: "8px 10px", fontSize: "0.8rem" }}
                />
              </Box>
            ) : null}
          </Box>
        ))}
      </Box>

      <HSSectionLabel right={`${data.photos?.length || 0} / 3 captured`}>Photos</HSSectionLabel>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" }}>
        {PHOTO_SLOTS.map(({ slot, label }) => {
          const photo = photosBySlot[slot];
          return (
            <Box
              key={slot}
              onClick={() => handlePhotoTileClick(slot)}
              sx={{
                aspectRatio: "4/3",
                borderRadius: "10px",
                border: photo ? `1px solid ${BORDER}` : "2px dashed rgba(255,255,255,0.12)",
                backgroundColor: photo ? "transparent" : CARD_BG,
                backgroundImage: photo ? `url(${resolveMediaUrl(photo.image)})` : "none",
                backgroundSize: "cover",
                backgroundPosition: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                cursor: "pointer",
                overflow: "hidden",
                position: "relative",
                transition: "border-color 0.15s, background 0.15s",
                "&:hover": photo ? undefined : { borderColor: "rgba(253,185,64,0.35)", backgroundColor: "rgba(253,185,64,0.03)" },
              }}
            >
              {!photo ? (
                <>
                  <Box sx={{ width: "36px", height: "36px", borderRadius: "9px", backgroundColor: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <CameraAltOutlinedIcon sx={{ fontSize: "1.25rem" }} />
                  </Box>
                  <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.75)", fontWeight: 600 }}>{label}</Typography>
                  <Typography sx={{ fontSize: "0.66rem", color: "rgba(255,255,255,0.4)" }}>Tap to capture</Typography>
                </>
              ) : (
                <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, backgroundColor: "rgba(13,17,23,0.75)", py: "3px", textAlign: "center" }}>
                  <Typography sx={{ fontSize: "0.62rem", color: "#fff", fontWeight: 600 }}>{label}</Typography>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      <HSNotesBlock
        value={sectionNotes}
        onChange={(e) => setSectionNotes(e.target.value)}
        sub="Anything off-checklist worth recording — flags raised by installers, observed defects, follow-ups."
        placeholder="e.g. Left rear indicator intermittent — booked in for Wed PM at fleet depot."
      />

      <HSFooterActions onSaveExit={handleSaveExit} onMarkComplete={handleMarkComplete} markCompleteDisabled={isSaving} />

      {odometerModalOpen ? (
        <OdometerDialog value={odometerKm} isSaving={isSaving} onClose={() => setOdometerModalOpen(false)} onSave={handleOdometerSave} />
      ) : null}
      {confirmDialog}
    </HSPageShell>
  );
}

function MetaDot() {
  return <Box component="span" sx={{ width: "4px", height: "4px", borderRadius: "50%", backgroundColor: "rgba(255,255,255,0.3)", flexShrink: 0 }} />;
}
